import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";

// Actions
import {
  setCategory,
  setProviderFilter,
  setProviderPage,
} from "redux/game/slice";

// Components
import TopNavigationMobileItem from "components/molecules/TopNavigationMobileItem";

// Constants
import { CATEGORIES_ENUMS } from "constants/navigation/categories";
import { NAVIGATION_PROPERTIES } from "constants/navigation/topNavigationDesktop";
import {
  TOP_NAVIGATION_ID_MOBILE_ENUM,
  TopNavigationMobileTypes,
} from "constants/navigation/topNavigationMobile";
import { GET_PARAMS } from "constants/popupConfig";

// Hooks
import { useCheckBalance } from "hooks/useCheckBalance";
import { useGTM } from "hooks/useGTM";

// Types
import { GAME_MODE } from "types/Games";

import { makeLangUrl } from "helpers/makeLangUrl";
import { useRouterHistoryStore } from "stores/routerHistory";
import url from "url/index";

type TopNavigationMobileItemContainerProps = {
  item: TopNavigationMobileTypes;
};

const TopNavigationMobileItemContainer = (
  props: TopNavigationMobileItemContainerProps,
) => {
  const { item } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const checkBalance = useCheckBalance(
    item.id !== TOP_NAVIGATION_ID_MOBILE_ENUM.LIVE_CASINO &&
      item.id !== TOP_NAVIGATION_ID_MOBILE_ENUM.TV_GAMES,
  );
  const GTM = useGTM();

  const setPreviousPage = useRouterHistoryStore(
    (state) => state.setPreviousPage,
  );

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const navigateToGame = (item) => {
    navigate({
      pathname: makeLangUrl(url.routes.casino.game({ game: item.urlAlias })),
      search: `?${createSearchParams({ [GET_PARAMS.MODE]: GAME_MODE.REAL })}`,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const dispatchProviderFilters = (item) => {
    dispatch(setProviderFilter(null));
    dispatch(setProviderPage(item.value.join(",")));
    dispatch(setCategory(CATEGORIES_ENUMS.LOBBY));
  };

  const handleInitReal = useCallback(() => {
    dispatch(setProviderFilter(null));
    // eslint-disable-next-line default-case
    switch (item.id) {
      case TOP_NAVIGATION_ID_MOBILE_ENUM.LIVE_CASINO:
        GTM.clickLiveCasino();
        dispatch(setCategory(CATEGORIES_ENUMS.LIVE_CASINO));
        break;
      case TOP_NAVIGATION_ID_MOBILE_ENUM.AVIATOR:
      case TOP_NAVIGATION_ID_MOBILE_ENUM.CHICKEN_ROAD:
      case TOP_NAVIGATION_ID_MOBILE_ENUM.CRAZY_TIME: {
        const isBalancePositive = checkBalance();
        if (isBalancePositive) {
          navigateToGame(item);
        }
        break;
      }
      case TOP_NAVIGATION_ID_MOBILE_ENUM.TV_GAMES:
        GTM.clickTvGames();
        break;
      case TOP_NAVIGATION_ID_MOBILE_ENUM.GAMES:
        dispatch(setProviderFilter(item?.value?.[0] || null));
    }

    if (item.link) {
      navigate(makeLangUrl(item.link));
    }

    if (item.property === NAVIGATION_PROPERTIES.PROVIDER && item.value) {
      dispatchProviderFilters(item);
    }

    setPreviousPage(`${location.pathname}${location.search}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, navigate, checkBalance, GTM, dispatch, location, setPreviousPage]);

  return (
    <TopNavigationMobileItem
      navigationItem={item}
      onInitReal={handleInitReal}
    />
  );
};

export default TopNavigationMobileItemContainer;
